/**
 * JSCAD Web Application - Main Entry Point
 *
 * This module wires together the application components:
 * - View/Camera controls
 * - Worker communication
 * - File system handling
 * - Parameter UI
 * - Script loading
 */

// Global error handlers - catch unhandled errors and rejections
window.addEventListener('error', (event) => {
  console.error('Unhandled error:', event.error)
})

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason)
})

// External dependencies
import { Gizmo } from '@jscadui/html-gizmo'
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- OrbitState used in JSDoc types
import { OrbitControl, OrbitState } from '@jscadui/orbit'
import { boundingBox } from '@jscadui/format-common'

// Local modules
import defaultCode from './examples/jscad/jscad-examples/01-two-cars.example.js'
import hierarchicalCarCode from './examples/jscad/jscad-examples/02-hierarchical-car.example.js'
import { addV1Shim } from '@jscadui/worker/src/addV1Shim.js'
import * as editor from './src/editor.js'
import { confirm } from './src/ui/dialog.js'
import * as engine from './src/engine.js'
import * as exporter from './src/exporter.js'
import * as menu from './src/menu.js'
import { currentBuildHash, watchBuildHash } from './src/buildVersion.js'
import * as remote from './src/remote.js'
import { setError } from './src/error.js'
import { ViewState } from './src/viewState.js'
import * as welcome from './src/welcome.js'
import * as about from './src/about.js'
import { showTrustedSourcesDialog, trustedSourcesStyles } from './src/trustedSourcesUI.js'
import { showDemoBrowser, demoBrowserStyles } from './src/demoBrowser.js'
import { showPartsBrowser, partsBrowserStyles } from './src/partsBrowser.js'

// Extracted modules
import { updatePipelineStats, countGeometry, createProgressHandler } from './src/stats.js'
import { capGeometry, DEFAULT_CAPS } from './src/caps.js'
import { createProjectTools } from './src/aiDeps.js'
import { installHarnessHook } from './src/harnessHook.js'
import { createViewHandler } from '@jscadui/agent-loop/src/viewHandler.js'
import { renderViews } from './src/aiViewRender.js'
import { createProjectBuilds, projectEntry } from './src/projectBuild.js'
import { createExport } from './src/aiExport.js'
// Nothing here may import ./src/storage/schema.js: its zod 4 types are more
// than the root TS 4.9 gate can parse (see root tsconfig).
import { createLocalStorage } from './src/storage/local.js'
import { assembleFileMap } from './src/storage/map.js'
import { createProjectManager } from './src/storage/projects.js'
import { createSession } from './src/storage/session.js'
import { chatVersionMessage, EDIT_COALESCE_MS, excerptOf } from './src/storage/versioning.js'
import { initEditorHeader } from './src/editorHeader.js'
import { extractEntries, readAsText, readDir } from '@jscadui/fs-provider'
import { createFrame, createJobTracker } from './src/frameSetup.js'
import { createProjectSwitch, defaultProjectFiles, isRecordablePath, loadSource, pickBootProject, projectPathOf, shouldRestoreBootProject, toEditorFiles } from './src/projectFiles.js'
import { createDiskProject, diskEntryFromHash, DISK_ID } from './src/diskProject.js'
import { createScriptRuns, sendScript } from './src/scriptRuns.js'
import { createStreamRuns } from './src/streamRuns.js'
import { newRunId } from './src/runId.js'
import { bindStopButton } from './src/runStop.js'
import { createMeshRefs } from './src/meshRefs.js'
import { PROJECT_BASE } from './src_frame/fileMap.js'
import * as fileSystem from './src/fileSystem.js'
import * as paramsUI from './src/paramsUI.js'
import { clearReloadTimestamp } from './src/reloadDetection.js'
import { missingSaveHandleMessage, saveRoute } from './src/saveFile.js'
import { createDocs, createIndexLoader } from './src/apiIndex.js'
import { showApiHelp, apiHelpStyles } from './src/apiHelp.js'
import { initChat } from './src/aiChat.js'
import { createWebSearchHandler } from './src/aiSearch.js'
import { createWebFetchHandler } from './src/aiFetch.js'
import { dispatchTool, NO_ENTRY, SKILLS, skillTool } from '@jscadui/agent-loop'
import { getChatApi, getProviderConfig, getWebsearchSelection, initAccount, searchStores } from './src/aiAccount.js'
import { getSession } from './src/syncEntry.js'

/**
 * @typedef {import('@jscadui/worker').JscadWorker} JscadWorker
 */

/**
 * @param {string} id
 * @returns {HTMLElement}
 */
export const byId = id => /** @type {HTMLElement} */(document.getElementById(id))

// Use origin + '/' as base to ensure proper URL resolution
const appBase = location.origin + '/'
let currentBase = appBase

// ============== View & Camera Setup ==============
const viewState = new ViewState()
const gizmo = new Gizmo()
byId('layout').append(gizmo)

// Load default model unless another model was already loaded
let loadDefault = true

// ============== Menu & Welcome ==============
// Wired before the frame boot below: none of the chrome needs the frame, and a
// click landing during those awaits would be dropped rather than queued.
menu.init({
  onBrowseDemos: () => showDemoBrowser({
    baseUrl: new URL('./examples/', appBase).toString(),
    onLoad: async (script, url) => {
      if (!(await editor.confirmDiscard())) return
      editor.setSource(script, url)
      jscadScript({ script, url, base: new URL('./', new URL(url, appBase)).toString(), root: appBase })
      welcome.dismiss()
    },
  }),
  onBrowseParts: () => showPartsBrowser({
    catalogUrl: new URL('./parts/catalog.json', appBase).toString(),
    // editor.init() runs later, after the frame boot above; Insert must not
    // call into a CodeMirror view that doesn't exist yet.
    getEditor: () => (editor.isReady() ? editor : null),
  })
})
welcome.init()
about.init()

// Build hash in the Options line, and a reload affordance when a deploy lands
// mid-session: entry documents are no-cache, but long-lived tabs keep
// running the frame they booted with.
{
  const versionEl = byId('build-version')
  const bootHash = await currentBuildHash().catch(() => null)
  if (bootHash) {
    console.log('build', bootHash)
    if (versionEl) {
      versionEl.textContent = bootHash
      versionEl.title = `this page runs build ${bootHash}`
    }
    watchBuildHash({
      current: bootHash,
      onNewVersion: (latest) => {
        if (!versionEl) return
        versionEl.textContent = 'New version'
        versionEl.title = `build ${latest} is served: click to reload`
        versionEl.classList.add('update-available')
        versionEl.onclick = () => location.reload()
      },
    })
  }
}

// Trusted Sources dialog
const trustedSourcesBtn = byId('trusted-sources-btn')
if (trustedSourcesBtn) {
  trustedSourcesBtn.addEventListener('click', showTrustedSourcesDialog)
}

// Inject dialog styles
const trustedStyles = document.createElement('style')
trustedStyles.textContent = trustedSourcesStyles + '\n' + demoBrowserStyles + '\n' + partsBrowserStyles + '\n' + apiHelpStyles
document.head.appendChild(trustedStyles)

const ctrl = new OrbitControl([byId('viewer')], { ...viewState.camera })

/** @param {OrbitState} change */
const updateFromCtrl = change => {
  const { position, target, rx, rz } = change
  viewState.setCamera({ position, target })
  gizmo.rotateXZ(rx, rz)
}
updateFromCtrl(ctrl)

ctrl.onchange = (/** @type {OrbitState} */ state) => viewState.saveCamera(state)
ctrl.oninput = (/** @type {OrbitState} */ state) => updateFromCtrl(state)
gizmo.onRotationRequested = (/** @type {string} */ cam) => ctrl.animateToCommonCamera(cam)

// ============== Stats & Progress ==============
const statsContent = byId('stats-content')
const progress = /** @type {HTMLProgressElement} */ (byId('progress'))
const onProgress = createProgressHandler(progress)

/** @type {{ setRunning: (running: boolean) => void, setCells: (count: number) => void } | null} */
let stopCtl = null

// ============== Params Controller ==============
const paramsCtrl = paramsUI.initParamsController()
// The frame's worker defaults to the flat params form; the app always uses the
// proxy, so it must say so on every init.
const useParamsProxy = true

// ============== Compute Frame Setup ==============

/**
 * Handle entities from worker
 * @param {{entities:unknown | Array<unknown>,treeTime:number,execTime:number,convTime:number}} result
 * @param {{skipLog?:boolean }} options
 * @returns {unknown} the error, when the result was refused rather than drawn
 */
const handleEntities = (result, { skipLog } = {}) => {
  if (result?.streamed) {
    // null for another run's result, or one a cap error already ended
    const totals = streamRuns.finish(result.runId, result.lost)
    if (!totals) return streamError ?? undefined
    meshRefs.remember(streamDrawn)
    onProgress(undefined)
    document.documentElement.dataset.vertices = String(totals.vertices)
    if (!totals.lost) setError(undefined)
    updatePipelineStats(statsContent, { treeTime: result.treeTime, triangles: totals.triangles, vertices: totals.vertices })
    if (!skipLog) console.debug('streamed', totals.cells, 'cells, tree:', result.treeTime?.toFixed(2))
    return
  }
  streamRuns.discard()
  const { entities: rawEntities, treeTime, execTime, convTime } = result

  // Refuse to draw past the caps before any allocation for rendering.
  let entities
  try {
    entities = capGeometry(meshRefs.resolve(rawEntities instanceof Array ? rawEntities : [rawEntities]), DEFAULT_CAPS)
  } catch (error) {
    setError(error)
    onProgress(undefined)
    return error
  }

  // Track render time
  const renderStart = performance.now()
  viewState.setModel(entities)
  const renderTime = performance.now() - renderStart
  meshRefs.remember(entities)

  if (viewState.zoomToFit) {
    const { min, max } = boundingBox(entities)
    const { fov, aspect } = viewState.viewer.getCamera()
    ctrl.fit(min, max, fov, aspect, 1 / 0.6)  // model fills ~60% of viewport
  }

  if (!skipLog) {
    console.debug('tree:', treeTime?.toFixed(2), ', exec:', execTime?.toFixed(2), ', conv:', convTime?.toFixed(2), ', render:', renderTime?.toFixed(2), entities)
  }

  // Read by the render sweep, which must tell an empty model from a render
  const { triangles, vertices } = countGeometry(entities)
  document.documentElement.dataset.vertices = String(vertices)

  setError(undefined)
  onProgress(undefined)

  updatePipelineStats(statsContent, { treeTime, execTime, convTime, renderTime, triangles, vertices })
}

const meshRefs = createMeshRefs()

// Remembered only when the run finishes: its later batches still refer to the previous model's meshes.
let streamDrawn = []

// A grid draws cell by cell; the camera refits to what is drawn, so it only zooms out.
const drawStream = (entities, box, totals) => {
  viewState.setModel(entities)
  streamDrawn = entities
  // Cumulative geometry counters: the stats panel would otherwise show the
  // previous model until the run finishes.
  if (totals) {
    document.documentElement.dataset.vertices = String(totals.vertices)
    updatePipelineStats(statsContent, { triangles: totals.triangles, vertices: totals.vertices })
  }
  if (viewState.zoomToFit && box) {
    const { fov, aspect } = viewState.viewer.getCamera()
    ctrl.fit(box.min, box.max, fov, aspect, 1 / 0.6)
  }
}

// The error that ended the current streamed run, which its load reports as the build's.
let streamError = null

const streamRuns = createStreamRuns({
  draw: drawStream,
  resolve: meshRefs.resolve,
  // Read by the render sweep, which restarts its hang guard on each cell
  onCells: count => {
    document.documentElement.dataset.cells = String(count)
    stopCtl?.setCells(count)
  },
  onError: error => {
    streamError = error
    setError(error)
    onProgress(undefined)
  },
})

/**
 * @param {() => boolean} isStale
 * @returns {string} the runId to send with the request, which the worker echoes on its cells
 */
const beginStream = isStale => {
  const runId = newRunId()
  delete document.documentElement.dataset.cells
  // A new run starts at zero cells: clearing the count stops the previous run's
  // total from showing until the first batch of the new one arrives.
  stopCtl?.setCells(null)
  streamError = null
  streamRuns.begin(isStale, runId)
  return runId
}

const trackJobs = createJobTracker(progress, onProgress, (jobs) => stopCtl?.setRunning(jobs > 0))

// Every model — the editor's and the agent's — runs in the sandboxed frame.
// Its opaque origin means no cookies, no storage and no same-origin fetch.
/* global __FRAME_ORIGIN__ */
const { workerApi, handlers, destroy: destroyFrame } = await createFrame({
  onError: setError,
  onEntities: handleEntities,
  onJobCount: trackJobs,
  onCells: (entities, runId) => streamRuns.accept(entities, runId),
  // No onTerminated re-init: frameSetup replays the inits itself, and an init
  // sent on every restart loops forever when the init is what timed out.
  runOrigin: __FRAME_ORIGIN__,
})

stopCtl = bindStopButton({ button: byId('stop-button'), workerApi, onError: setError })

// Twice the old agent cap: the heaviest examples in the corpus finish well
// inside it, and a runaway model still dies rather than wedging the frame.
// The render sweep raises it: there the point is whether a model is correct,
// not whether it is quick, and the cap is only there to stop a hang.
const DEFAULT_EDITOR_TIMEOUT_MS = 120_000
const EDITOR_TIMEOUT_MS = (() => {
  try {
    const stored = Number(localStorage.getItem('engine.modelTimeoutMs'))
    return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_EDITOR_TIMEOUT_MS
  } catch (err) {
    console.warn('could not read engine.modelTimeoutMs:', err)
    return DEFAULT_EDITOR_TIMEOUT_MS
  }
})()

// The render sweep pins the frame's worker count to compare a pooled grid with one worker.
const readPoolSize = () => {
  try {
    const stored = Number(localStorage.getItem('engine.poolSize'))
    return Number.isInteger(stored) && stored > 0 ? stored : undefined
  } catch (err) {
    console.warn('could not read engine.poolSize:', err)
    return undefined
  }
}

// The api index loads on the first docs call or error hint that needs it.
const loadApiIndex = createIndexLoader()

// The report of the open project's last build, for the chat's header and its
// measure, check and export; see src/projectBuild.js.
const projectBuilds = createProjectBuilds({
  measure: () => workerApi.jscadMeasure({ options: {} }),
  check: () => workerApi.jscadCheck({ options: {} }),
  getApi: getChatApi,
  loadIndex: loadApiIndex,
  getFiles: () => fileSystem.projectFiles(),
})

// The frame names its own bundles; the app names only the engine.
const initFrame = () =>
  workerApi
    .jscadInit({ engine: viewState.modelingEngine, useParamsProxy, timeoutMs: EDITOR_TIMEOUT_MS, poolSize: readPoolSize() })
    .catch(setError)

// A project's files travel to the frame in a map keyed by bare path, so a
// project script names itself against PROJECT_BASE; the app origin's /swfs/
// URLs are unreachable from the frame.
/** @param {string} path */
const projectUrls = path => ({
  url: PROJECT_BASE + String(path).replace(/^\//, ''),
  base: PROJECT_BASE,
  root: PROJECT_BASE,
})

// ============== File System Setup ==============
const dropModal = byId('dropModal')

/** @type {import('./src/fileSystem.js').FileSystemDeps} */
const fsDeps = {
  onFilesChange: () => reloadProject().catch(err => setError(err)), // Handle floating promise
  setEditorFiles: files => {
    editor.setFiles(files, { keepBuffers: true })
    refreshFileMeta()
  },
  onFilesChanged: files => editor.filesChanged(files),
  setError,
  onAliasFound: alias => workerApi.jscadInit({ alias }),
  onScriptReady: (script, url) => {
    currentEntry = url
    jscadScript(projectUrls(url))
    editor.setSource(script, url)
  },
  setProjectName: name => { exporter.exportConfig.projectName = name },
  addV1Shim,
  clearFileCache: files => workerApi.jscadClearFileCache({ files, root: PROJECT_BASE })
}

async function reloadProject() {
  workerApi.jscadClearTempCache()
  await fileSystem.reloadProject(fsDeps)
}

// Version/hash record for every editor compile and chat turn against the
// live project; the session routes by the manager's per-project mode.
const localStore = createLocalStorage()
let rowboatStore = null
// Only the launcher's build (JSCAD_LOCAL_FS=1) has /api/fs, the model directory it serves.
/* global __LOCAL_FS__ */
const diskStore = __LOCAL_FS__ ? (await import('./src/storage/disk.js')).createDiskStorage() : null
const projectManager = createProjectManager({ local: localStore, disk: diskStore, getRowboat: () => rowboatStore })
const storageSession = createSession({ local: localStore, disk: diskStore, getRowboat: () => rowboatStore, getBackend: (projectId) => projectManager.peekMode(projectId) })

// The editor drawer header mounts after the sync loop starts; callbacks reach
// it lazily. Every version-minting write refreshes its versions dropdown.
let editorHeader = null
const refreshHeader = () => {
  if (!editorHeader) return
  editorHeader.render().catch((err) => console.warn('header refresh failed:', err))
}

// Editing a helper leaves the entry as it is; `entry` names a model the user ran on its own.
// A burst of compiles folds into one `edit` row (five idle minutes):
// `coalesceKey` names the run and `coalesceMs` its window.
const recordEdit = async (script, path, { entry } = {}) => {
  if (!isRecordablePath(path)) return
  await storageSession.writeThrough(currentProjectId, path, script, { message: 'edit', coalesceKey: 'edit', coalesceMs: EDIT_COALESCE_MS, ...(entry ? { entry } : {}) }).catch((err) => console.warn('storage write failed:', err))
  refreshHeader()
}

let currentProjectId = 'default'
// The entry of whatever was opened last: a stored project or a dropped folder.
let currentEntry

// The paths the current agent turn wrote (collected as showFile runs, cleared
// when the next turn starts) and the tree's entry/touched marks.
const turnTouched = new Set()
const refreshFileMeta = () => editor.setFileMeta({ entry: currentEntry, touched: [...turnTouched] })

// Assigned below, after the switch callback is defined; null until the chat
// mounts, so an early switch (a launcher model hash) skips it.
let chatControls = null

// The editor drawer header asks the discard confirm itself before it hands a
// switch to the app, so the open that follows skips the switch's own ask: one
// navigation, one prompt.
let discardAnswered = false

/**
 * Run a header-driven open with the header's discard answer already given.
 * @param {() => Promise<unknown>} open
 * @returns {Promise<unknown>}
 */
const afterHeaderDiscard = (open) => {
  discardAnswered = true
  return open().finally(() => { discardAnswered = false })
}

const switchProject = createProjectSwitch({
  manager: projectManager,
  fileSystem,
  editor: { ...editor, confirmDiscard: () => (discardAnswered ? Promise.resolve(true) : editor.confirmDiscard()) },
  clearTempCache: () => workerApi.jscadClearTempCache(),
  build: (entry) => buildProject(entry),
  onOpen: (id, entry, name) => {
    // Leaving a stored project: mint one `edit` row for whatever it holds, but
    // only when that differs from its latest version, so a switch that changed
    // nothing adds no history. A disk project keeps none (no-op).
    const previous = currentProjectId
    if (previous !== id) {
      storageSession.snapshotUnique(previous, { message: 'edit' }).catch((err) => {
        if (err?.name !== 'NotFoundError') console.warn('snapshot on switch failed:', err)
      })
    }
    currentProjectId = id
    currentEntry = entry
    if (typeof name === 'string' && name) exporter.exportConfig.projectName = name
    refreshFileMeta()
    chatControls?.switchTo(id)
  },
  onError: setError,
})

const diskProject = diskStore && createDiskProject({
  store: diskStore,
  switchProject,
  isOpen: () => currentProjectId === DISK_ID,
  getEntry: () => currentEntry,
  fileSystem,
  clearFileCache: (files) => workerApi.jscadClearFileCache({ files, root: PROJECT_BASE }),
  editor,
  rebuild: () => buildProject().catch(setError),
})

// Lazy rowboat backend: built once a session exists, so anonymous users stay
// local-only and never load the rowboat client. Dynamic imports keep the
// TS 4.9 gate green (see above) and the rowboat bundle out of anonymous loads.
let rowboatStorePromise = null
let syncHandle = null

const fetchSyncToken = async () => {
  try {
    const res = await fetch('/api/sync-token', { credentials: 'include' })
    if (!res.ok) return null
    const body = await res.json()
    return body?.token ? body : null
  } catch (err) {
    console.warn('could not fetch the sync token:', err)
    return null
  }
}

const getRowboatStore = async () => {
  if (!rowboatStorePromise) {
    rowboatStorePromise = (async () => {
      const user = await getSession()
      if (!user?.id) return null
      const info = await fetchSyncToken()
      if (!info) return null
      const { createRowboatStorage } = await import('./src/storage/rowboat.js')
      rowboatStore = createRowboatStorage({
        syncBase: info.syncBase,
        identity: user.id,
        getHeaders: async () => {
          const refreshed = await fetchSyncToken()
          return refreshed ? { authorization: `Bearer ${refreshed.token}` } : {}
        },
      })
      return rowboatStore
    })().catch((err) => {
      console.warn('rowboat init failed:', err)
      rowboatStorePromise = null
      return null
    })
  }
  return rowboatStorePromise
}

// Interval table sync while signed in; anonymous users never reach it.
const initSyncLoop = async () => {
  const store = await getRowboatStore()
  if (!store) return
  const { createSyncLoop } = await import('./src/storage/sync.js')
  syncHandle = createSyncLoop({
    storage: store,
    getToken: async () => (await fetchSyncToken())?.token ?? null,
    onError: (err) => {
      console.warn('rowboat sync failed:', err)
      editorHeader?.setSyncStatus('Sync failed')
    },
    onSync: () => editorHeader?.setSyncStatus(''),
  })
  syncHandle.start()
}

initSyncLoop()

fileSystem.setupDragDrop(dropModal, async (dataTransfer, target) => {
  // A real DataTransfer is cleared once the drop event's dispatch unwinds,
  // so read it once, up front, and reuse the entries for every route.
  const entries = await extractEntries(dataTransfer)
  const row = target?.closest?.('[data-project-id]')
  if (row) {
    await projectManager.mergeDrop(row.dataset.projectId, entries, { readDir, readAsText }).catch(setError)
    if (row.dataset.projectId === currentProjectId) await switchProject(currentProjectId).catch(setError)
    return
  }
  await fileSystem.handleFileDrop(dataTransfer, fsDeps, entries)
  if (entries.length === 1 && entries[0].isDirectory) {
    const created = await projectManager.createFromDrop(entries, { readDir, readAsText }).catch(setError)
    if (created) await switchProject(created.id).catch(setError)
  }
})

const scriptRuns = createScriptRuns()

// A superseded update must not draw: a whole result would also discard the newer run.
const modelUpdateDeps = () => {
  let isStale = () => false
  let runId
  return {
    workerApi,
    handleEntities: (result, options) => { if (!isStale()) handlers.entities(result, options) },
    setError,
    beginRun: () => {
      isStale = scriptRuns.paramChange()
      runId = beginStream(isStale)
      return runId
    },
    endRun: () => streamRuns.end(runId),
    discardRun: () => streamRuns.discard(),
    held: meshRefs.held,
  }
}

// The Reset control drops the user's edits: values return to the code's
// defaults, the tree collapses, and the model redraws them.
const resetParams = () => {
  const tree = paramsUI.getParamsTreeView()
  if (!tree) return
  paramsCtrl.params = { ...tree.reset() }
  paramsCtrl.userInteracted.clear()
  paramsUI.scheduleModelUpdate(() => paramsUI.runModelUpdate(modelUpdateDeps()))
}

// The zoom-to-fit toggle fits the camera to the model on screen; the model is
// not re-run for a camera change.
const fitViewToModel = () => {
  const entities = viewState.model
  if (!entities?.length) return
  const { min, max } = boundingBox(entities)
  const { fov, aspect } = viewState.viewer.getCamera()
  ctrl.fit(min, max, fov, aspect, 1 / 0.6)
}

viewState.onZoomToFit = fitViewToModel

// ============== Script Loading ==============

let lastScriptUrl
let lastLoadSource

const STALE = { stale: true }

/**
 * Load a script and draw it. Resolves to the load's outcome, `{ result }` or
 * `{ error }`, or `{ stale: true }` when a newer load replaced it; a load of a
 * project file is recorded as the project's build.
 * @param {{script?:string,url?:string,base?:string,root?:string,source?:string}} options
 */
const jscadScript = async ({ script, url = './jscad.model.js', base = currentBase, root }) => {
  const isStale = scriptRuns.load()
  const settled = (outcome) => {
    if (isStale()) return STALE
    projectBuilds.recordLoad(url, outcome)
    return outcome
  }
  if (url !== lastScriptUrl) meshRefs.forget()
  lastScriptUrl = url
  let runId = beginStream(isStale)
  currentBase = base
  loadDefault = false
  document.documentElement.dataset.render = 'running'
  delete document.documentElement.dataset.vertices

  // Capture the params, edits and expansion to carry: only a re-run of the
  // same model carries, so a freshly opened one starts from the new code's
  // defaults. The load identity is loadSource's: the url for a URL script,
  // `project:<projectId>` for a project file.
  const loadSourceId = loadSource({ url, projectId: currentProjectId })
  const sameModel = loadSourceId === lastLoadSource
  lastLoadSource = loadSourceId

  const savedParams = { ...paramsCtrl.params }
  const savedUserInteracted = [...paramsCtrl.userInteracted]
  const savedExpanded = paramsUI.getParamsTreeView()?.getExpandedPaths() ?? []

  // Reset controller and UI
  paramsCtrl.reset()
  paramsUI.destroyParamsTreeView()

  // A blank script (a new project's main.js) draws nothing: run, it fails as
  // NoMainError and the error path keeps the previous model on screen.
  if (typeof script === 'string' && script.trim() === '') {
    streamRuns.end(runId)
    if (isStale()) return STALE
    byId('paramsDiv').innerHTML = ''
    viewState.setModel([])
    meshRefs.remember([])
    updatePipelineStats(statsContent, { triangles: 0, vertices: 0 })
    document.documentElement.dataset.vertices = '0'
    setError(undefined)
    if (url.startsWith(PROJECT_BASE)) projectBuilds.recordNoEntry()
    else projectBuilds.recordLoad(url, {})
    return {}
  }

  try {
    // Mixed local/rowboat models merge at load time: rowboat project files
    // land in the worker's file cache (its require path), each manifest path
    // naming exactly one backend. Unlisted sibling requires resolve
    // local-first through the service worker, then this rowboat cache.
    try {
      const store = await getRowboatStore()
      if (store) {
        const project = await store.readProject(currentProjectId).catch(() => null)
        if (project) {
          const manifest = Object.fromEntries(Object.keys(project.files).map((path) => [path, 'rowboat']))
          const merged = assembleFileMap(manifest, { local: {}, rowboat: project.files })
          for (const [path, content] of Object.entries(merged)) {
            await fileSystem.addToCacheWrapper(path, content)
          }
        }
      }
    } catch (err) {
      console.warn('rowboat merge failed:', err)
    }
    // Query renderer capability for GPU normals support
    const useGpuNormals = viewState.viewer?.supportsGpuNormals ?? false
    const files = await fileSystem.projectFiles()
    if (isStale()) return STALE
    const result = await sendScript(workerApi, files, { script, url, base, root, source: loadSource({ url, projectId: currentProjectId }), useGpuNormals, runId, held: meshRefs.held(), supersede: true }, getChatApi())
    if (isStale()) return STALE

    if (result.proxyState) {
      paramsCtrl.initFromResult(result)

      // Restore the user's edits where the new code still declares the param,
      // so running the same model does not reset them to the code's defaults.
      const defaults = { ...paramsCtrl.params }
      const carried = paramsUI.carriedFromPrevious({
        sameModel,
        previous: savedParams,
        userInteracted: savedUserInteracted,
        expandedPaths: savedExpanded,
        defaults,
      })
      Object.assign(paramsCtrl.params, carried.values)
      for (const path of carried.userInteracted) {
        paramsCtrl.userInteracted.add(path)
      }

      // Setup UI
      const paramsHeader = byId('paramsHeader')
      const paramsDiv = byId('paramsDiv')
      paramsDiv.innerHTML = ''

      const { showHiddenCheckbox } = paramsUI.buildParamsHeader(paramsHeader, { onReset: resetParams })

      // Tree container
      const treeContainer = document.createElement('div')
      treeContainer.id = 'paramsTreeContainer'
      paramsDiv.appendChild(treeContainer)

      const state = paramsCtrl.getState()
      const paramsTreeView = paramsUI.createParamsTreeUI({
        target: treeContainer,
        proxyState: result.proxyState,
        state,
        expandedPaths: carried.expandedPaths,
        onChange: (paramPath, value) => {
          paramsUI.handleTreeParamChange(paramPath, value, () => {
            paramsUI.scheduleModelUpdate(() => paramsUI.runModelUpdate(modelUpdateDeps()))
          })
        },
        onClassChange: (partPath, newClass, mode) => {
          paramsUI.handleTreeClassChange(partPath, newClass, mode, modelUpdateDeps())
        }
      })

      showHiddenCheckbox.onchange = () => {
        paramsTreeView?.setShowHidden(showHiddenCheckbox.checked)
      }

      // The run above drew the code's defaults; a carried value that differs
      // needs one more run to draw.
      const carriedChanged = Object.entries(carried.values).some(([path, value]) => value !== defaults[path])
      if (carriedChanged) {
        runId = beginStream(isStale)
        const restoreResult = await workerApi.jscadMain({ ...paramsCtrl.getWorkerParams(), runId, held: meshRefs.held(), supersede: true })
        if (isStale()) return STALE
        const refused = handlers.entities(restoreResult)
        return settled(refused ? { error: refused } : { result })
      }
    }

    const refused = handlers.entities(result)
    return settled(refused ? { error: refused } : { result })
  } catch (err) {
    // A stop's AbortError drops the partial grid it interrupted instead of
    // flushing it; a stale run's stream already belongs to the newer run.
    if (err?.name === 'AbortError' && !isStale()) streamRuns.discard()
    else streamRuns.end(runId)
    if (isStale() || err?.name === 'SupersededError') return STALE
    setError(err)
    return settled({ error: err })
  }
}

/**
 * Build the open project: its entry (see projectEntry) run through the load
 * path above. The editor, a project switch and the chat's writes all build
 * this way, so each leaves the same report. The editor and a switch name the
 * file they open, which runs when it is a model of its own; the chat's builds
 * name none.
 * @param {string} [open]
 */
const buildProject = async (open) => {
  const files = await fileSystem.projectFiles()
  const entry = projectEntry(files, currentEntry, open)
  if (!entry) {
    projectBuilds.recordNoEntry()
    const error = Object.assign(new Error(NO_ENTRY), { name: 'NoEntryError' })
    setError(error)
    return { error }
  }
  // Files the chat wrote into an empty cache are the open project from then on.
  currentEntry ??= entry
  return jscadScript({ script: typeof files[entry] === 'string' ? files[entry] : undefined, ...projectUrls(entry) })
}

// ============== Engine Initialization ==============

// Initialize render engine first so we can query its capabilities
viewState.setEngine(await engine.init(viewState.renderEngine))

await initFrame()

// Set up engine change handler
viewState.onModelingEngineChange = async (newEngine) => {
  console.log('Switching modeling engine to:', newEngine)

  await initFrame()

  // Re-run script
  editor.runScript()
}

viewState.onWorkerCountChange = async (count) => {
  console.log('Switching worker count to:', count)

  await initFrame()

  // Re-run script so the new pool picks up the grid
  editor.runScript()
}

viewState.onRenderEngineChange = async (newEngine) => {
  console.log('Switching render engine to:', newEngine)

  // Destroy old viewer
  viewState.viewer?.destroy?.()

  // Initialize new viewer
  viewState.setEngine(await engine.init(newEngine))
  meshRefs.forget()
  const isStale = scriptRuns.paramChange()
  const runId = beginStream(isStale)

  // Re-run main with current params to regenerate geometry
  const useGpuNormals = viewState.viewer?.supportsGpuNormals ?? false
  const mainOptions = { ...paramsCtrl.getWorkerParams(), useGpuNormals, runId, held: meshRefs.held(), supersede: true }
  let result
  try {
    result = await workerApi.jscadMain(mainOptions)
  } catch (error) {
    streamRuns.end(runId)
    if (error?.name === 'SupersededError') return
    throw error
  }
  if (isStale()) return
  handlers.entities(result)
}

paramsUI.injectParamsStyles()

// ============== File Watching ==============
// Store fileWatcher reference for explicit cleanup (fallback beforeunload also exists)
const fileWatcher = fileSystem.createFileWatcher(
  files => editor.filesChanged(files),
  () => editor.runScript()
)

// ============== Editor Initialization ==============
editor.init(
  defaultCode,
  async (script, path) => {
    const swHandler = fileSystem.getSwHandler()
    // A dropped folder's editor paths keep their leading slash in the cache.
    const cachePath = swHandler?.fileToRun ? path : currentEntry === undefined ? null : projectPathOf(path)
    if (cachePath) {
      await fileSystem.addToCacheWrapper(cachePath, script)
      await workerApi.jscadClearFileCache({ files: [cachePath], root: PROJECT_BASE })
      const open = projectPathOf(path)
      // A model of its own that the user runs becomes the project's entry, so the project reopens on it.
      const standalone = projectEntry(await fileSystem.projectFiles(), currentEntry, open) === open
      if (standalone) currentEntry = cachePath
      await recordEdit(script, cachePath, standalone ? { entry: cachePath } : {})
      buildProject(open).catch(setError)
    } else {
      // With no project opened the path is an example's URL or the editor's placeholder.
      const fullUrl = path.startsWith('http') ? path : new URL(path, appBase).toString()
      const base = new URL('./', fullUrl).toString()
      await recordEdit(script, path)
      jscadScript({ script, url: path, base })
    }
  },
  async (script, path) => {
    try {
      if (await diskProject?.save(path, script)) return
    } catch (err) {
      setError(err)
      return
    }
    const swHandler = fileSystem.getSwHandler()
    const pathArr = path.split('/')
    let fileHandle = (await swHandler?.getFile(path))?.handle
    console.log('save file', path, fileHandle)

    const saveMap = fileSystem.getSaveMap()
    if (!fileHandle) fileHandle = saveMap[path]

    // A project path is already stored by the compile that ran it, so Ctrl+S
    // must not open a Save As picker for it; a dropped file's handle still
    // writes back to disk.
    const route = saveRoute({ inProject: isRecordablePath(path), hasHandle: !!fileHandle })
    if (route === 'project') return

    if (!fileHandle) {
      const opts = {
        suggestedName: pathArr[pathArr.length - 1],
        excludeAcceptAllOption: true,
        types: [
          {
            description: 'Javascript',
            accept: { 'application/javascript': ['.js'] },
          },
        ],
      }
      fileHandle = await globalThis.showSaveFilePicker?.(opts)
    }

    if (fileHandle) {
      const writable = await fileHandle.createWritable()
      await writable.write(script)
      await writable.close()
      fileSystem.setSaveMapEntry(path, fileHandle)
      fileHandle.lastMod = Date.now() + 500
      await recordEdit(script, path)
    } else {
      setError(new Error(missingSaveHandleMessage(path)))
    }
  },
  path => fileSystem.getSwHandler()?.getFile(path),
  confirm,
)

// Under the launcher a #/models/<entry> hash opens the model directory as the project.
const diskEntry = diskProject ? diskEntryFromHash(location.hash) : null
let hasRemoteScript = diskEntry !== null
if (diskEntry === null) {
  try {
    hasRemoteScript = await remote.init(
      async (script, url) => {
        if (!(await editor.confirmDiscard())) return
        const fullUrl = new URL(url, appBase).toString()
        editor.setSource(script, fullUrl)
        jscadScript({ script, url, base: appBase })
        welcome.dismiss()
      },
      err => {
        loadDefault = false
        setError(err)
        welcome.dismiss()
      },
    )
  } catch (e) {
    console.error(e)
  }
}

await exporter.init(workerApi, { onError: setError })

// ============== Default Script ==============
if (loadDefault && !hasRemoteScript) {
  const defaultUrl = './examples/jscad/jscad-examples/01-two-cars.example.js'
  const fullUrl = new URL(defaultUrl, appBase).toString()
  if (await editor.confirmDiscard()) {
    editor.setSource(defaultCode, fullUrl)
    jscadScript({ script: defaultCode, url: fullUrl, base: appBase })
  }
}

// ============== Service Worker Check ==============
// Models run in the compute frame, so a failed registration costs dropped-file
// watching, not the ability to run anything. Warn, never block.
try {
  if (!fileSystem.getSwHandler()) await fileSystem.initFs(fsDeps)
  clearReloadTimestamp()
} catch (err) {
  console.warn('file service worker unavailable; dropped-file watching is off', err)
}

if ('serviceWorker' in navigator && !navigator.serviceWorker.controller) {
  console.warn('file service worker not controlling this page; dropped-file watching is off')
}

// Opened only now: files cached before the service worker registers land in a memory map it no longer reads.
if (diskEntry !== null) {
  welcome.dismiss()
  await diskProject.open(diskEntry).catch(setError)
}

// ============== AI Chat ==============
// The agent loop runs in the page; each tool request is served against the
// open project's files (the file cache every run sends the frame), the
// editor, the viewer and the frame. See aiDeps.js.
// The agent's writes and deletes against the frame's file cache; the tree's
// own delete/rename go through the same pair.
const writeProjectFile = async (path, content) => {
  await fileSystem.addToCacheWrapper(path, content)
  await workerApi.jscadClearFileCache({ files: [path], root: PROJECT_BASE })
}
const removeProjectFile = async (path) => {
  await fileSystem.removeFromCache(path)
  await workerApi.jscadClearFileCache({ files: [path], root: PROJECT_BASE })
}
const showFiles = (files) => {
  editor.setFiles(toEditorFiles(files), { keepBuffers: true, resetMissing: true })
  refreshFileMeta()
}

const chatTools = createProjectTools({
  getProjectFiles: () => fileSystem.projectFiles(),
  writeProjectFile,
  removeProjectFile,
  showFile: (path, content, files) => {
    // A chat save is the turn's touch: mark the file for the tree.
    const touched = projectPathOf(path)
    if (touched) turnTouched.add(touched)
    editor.confirmDiscard().then((ok) => {
      if (!ok) return
      editor.setSource(content, path)
      editor.setFiles(toEditorFiles(files), { keepBuffers: true })
      refreshFileMeta()
    })
  },
  showFiles,
  build: async () => {
    const outcome = await buildProject()
    if (outcome.stale) return { ok: false, error: { name: 'SupersededError', message: 'a newer run replaced this build before it finished; write or edit again to build' } }
    return projectBuilds.report()
  },
  noGeometry: projectBuilds.noGeometry,
  recordGated: projectBuilds.recordReport,
  workerApi,
  exportModel: createExport((args) => workerApi.jscadExportData(args), { save: diskProject?.saveExport }),
  getProjectId: () => currentProjectId,
  // Stored at once, so a reload mid-turn keeps it; a failure fails the
  // write tool, so the model sees it and can retry instead of the cache
  // silently running ahead of storage.
  storeFile: (projectId, path, content) =>
    storageSession.writeThrough(projectId, path, content, { message: 'chat', version: false }),
  removeFile: (projectId, path) => storageSession.removeThrough(projectId, path),
  snapshot: async (projectId, excerpt) => {
    await storageSession.snapshot(projectId, { message: chatVersionMessage(excerptOf(excerpt ?? '')) })
    if (projectId === currentProjectId) refreshHeader()
  },
  // The files as they stood before the turn's first write, for the undo.
  // Folder projects keep history in git, so they pin nothing.
  pinTurnStart: async (projectId) => {
    if (projectManager.peekMode(projectId) === 'disk') return null
    try {
      return await storageSession.pinVersion(projectId, { message: 'before chat' })
    } catch (error) {
      // A project the backend never saw has nothing to pin; the write creates it.
      if (error?.name === 'NotFoundError') return null
      throw error
    }
  },
  getApi: getChatApi,
  loadIndex: loadApiIndex,
})

// The tree's row actions act on the current project only, through the same
// cache/storage pair the agent's tools use.
editor.setFileActions({
  onDelete: async (path) => {
    // Tree keys carry the editor's leading slash; storage and the frame cache
    // key project files by their bare path.
    const target = projectPathOf(path)
    if (!target) return
    await removeProjectFile(target)
    await storageSession.removeThrough(currentProjectId, target)
    showFiles(await fileSystem.projectFiles())
    buildProject().catch(setError)
  },
  onRename: async (path, name) => {
    const from = projectPathOf(path)
    if (!from || !name) return
    const dir = from.includes('/') ? from.slice(0, from.lastIndexOf('/') + 1) : ''
    const to = `${dir}${name}`.replace(/^\/+/, '')
    if (!to || to === from) return
    // Renaming the open file with unsaved edits reuses the discard gate: the
    // refresh below drops the old path's buffer.
    if (projectPathOf(editor.getPath()) === from && editor.isDirty() && !(await editor.confirmDiscard())) return
    const files = await fileSystem.projectFiles()
    if (Object.hasOwn(files, to)) {
      editor.showFileMessage(`“${to}” already exists`)
      return
    }
    const content = files[from]
    // Renaming the entry file moves the project's entry with it, or the next
    // build would look for a path that no longer exists.
    const renamingEntry = projectPathOf(currentEntry) === from
    if (renamingEntry) currentEntry = to
    await writeProjectFile(to, content)
    await storageSession.writeThrough(currentProjectId, to, content, { message: 'edit', coalesceKey: 'edit', coalesceMs: EDIT_COALESCE_MS, ...(renamingEntry ? { entry: to } : {}) })
    await removeProjectFile(from)
    await storageSession.removeThrough(currentProjectId, from)
    // The open file moved: point the editor at its new path so the refresh
    // below keeps it open instead of replacing it. Binary content never loads
    // into the text view, so only a string moves the cursor.
    if (typeof content === 'string' && projectPathOf(editor.getPath()) === from) editor.setSource(content, to)
    showFiles(await fileSystem.projectFiles())
    buildProject().catch(setError)
  },
})

const aiDeps = {
  ...chatTools,
  // The off-screen multi-view render: the live viewer's canvas is never read,
  // so a view call leaves the user's own camera and view exactly as they were.
  // A part-name aim resolves through the build's auto-attached anchors, so the
  // frame must return each solid's named frames: `measure` runs with anchors on
  // and reports the part-path frames the model tree stamped.
  view: createViewHandler({
    getMesh: () => viewState.model,
    noGeometry: projectBuilds.noGeometry,
    render: renderViews,
    measure: (options) => workerApi.jscadMeasure({ options }),
    env: () => ({ THREE: globalThis.THREE, smooth: viewState.smoothRender, bg: viewState.theme.bg }),
  }),
  docs: createDocs(loadApiIndex, getChatApi),
  skill: (args) => skillTool(SKILLS, args.name ?? args.id),
  // The question tool is served by the chat card; it can only arrive mid-turn,
  // when the control exists, but guard the not-yet-mounted case anyway.
  askUser: (input) => chatControls?.ask(input) ?? Promise.reject(new Error('chat not ready')),
  websearch: createWebSearchHandler({
    // The dialog's stores, not a second copy: session-custody keys live in
    // page memory, so a fresh store set would see none of them.
    stores: searchStores,
    getProjectId: () => currentProjectId,
    getPinned: () => getWebsearchSelection().provider,
  }),
  webfetch: createWebFetchHandler({}),
}

installHarnessHook({
  storage: globalThis.localStorage,
  target: globalThis,
  view: aiDeps.view,
  measure: chatTools.measure,
  check: chatTools.check,
  buildReport: () => projectBuilds.report(),
})

// Both drawer headers mount the same sign-in control and sign out together.
const accountDeps = {
  // The local backend persists to IndexedDB when available, so a reload keeps
  // this session's work; the unload warning applies only when it does not
  // (private mode, the compute frame), where a reload drops it. `isDirty`
  // stays false for a session that only booted the untouched default.
  hasLocalWork: () => localStore.isDirty() && !localStore.persists(),
  onSignOut: async () => {
    syncHandle?.stop()
    syncHandle = null
    try { await rowboatStore?.close() } catch (err) { console.warn('rowboat close failed:', err) }
    rowboatStore = null
    rowboatStorePromise = null
    editorHeader?.setSyncStatus('')
    await editorHeader?.render()
  },
}

if (byId('ai-account')) initAccount(byId('ai-account'), {
  hasLocalWork: accountDeps.hasLocalWork,
  // The blanket web-search grant covers the live conversation; revoking it
  // clears the grant but never the stored search keys.
  onWebsearchGrant: (on) => chatControls?.setWebsearchGranted(on),
  // Any provider/recording change rewrites the chat drawer's notice line.
  onSelectionChange: () => chatControls?.refreshNotice(),
  onSignOut: accountDeps.onSignOut,
})
if (byId('ai-chat')) {
  const chatStorage = {
    readConversation: (pid) => storageSession.readConversation(pid),
    writeConversation: (pid, messages) => storageSession.writeConversation(pid, messages),
  }
  chatControls = initChat({
    container: byId('ai-chat'),
    requestTool: (name, input) => dispatchTool(name, input, aiDeps),
    getProvider: getProviderConfig,
    getApi: getChatApi,
    // Blanket web-search permission: the dialog toggle sets the default for
    // new conversations; each granted call is audit-logged to the console.
    websearchGranted: getWebsearchSelection().allow === true,
    websearchLog: (entry) => console.info('[websearch]', entry),
    websearchProvider: () => getWebsearchSelection().provider,
    storage: chatStorage,
    projectId: () => currentProjectId,
    getProjectFiles: () => fileSystem.projectFiles(),
    getBuild: projectBuilds.report,
    startTurn: () => { turnTouched.clear(); refreshFileMeta(); chatTools.startTurn() },
    endTurn: chatTools.endTurn,
    restoreTurn: async (projectId, versionId) => {
      if (!(await editor.confirmDiscard())) return false
      await projectManager.restoreVersion(projectId, versionId)
      // The files are already restored, so the reopen must not ask again: a
      // second decline would leave the old editor text to overwrite them.
      await afterHeaderDiscard(() => switchProject(projectId))
      refreshHeader()
      return true
    },
  })
}
// The AI drawer keeps the right column to itself now that the project drawer
// folded into the editor drawer.
const aiDrawer = byId('ai-drawer')
const setDrawer = (drawer, open) => {
  drawer?.classList.toggle('closed', !open)
}
const toggleDrawer = (drawer) => setDrawer(drawer, drawer?.classList.contains('closed'))
byId('ai-toggle')?.addEventListener('click', () => toggleDrawer(aiDrawer))
byId('ai-chat-btn')?.addEventListener('click', () => {
  menu.dismiss()
  setDrawer(aiDrawer, true)
})
byId('api-help-btn')?.addEventListener('click', () => {
  menu.dismiss()
  showApiHelp({ loadIndex: loadApiIndex, getApi: getChatApi })
})

// ============== Editor Drawer Header ==============
const bootProjects = await projectManager.listAll()
if (shouldRestoreBootProject({ hasRemoteScript, diskEntry, projects: bootProjects })) {
  const bootId = pickBootProject(bootProjects)
  if (bootId) await switchProject(bootId).catch(setError)
} else if (bootProjects.length === 0) {
  currentProjectId = (await projectManager.createProject('default', { entry: 'main.js', files: defaultProjectFiles({ main: defaultCode, hierarchicalCar: hierarchicalCarCode }) })).id
}
// Boot is the baseline: everything the app itself created on load is not
// "work", so the reload warning keys off writes that came after it.
localStore.markClean()
editorHeader = initEditorHeader(byId('editor-header'), {
  manager: projectManager,
  // The header asked the discard confirm before calling this.
  onSwitch: (id) => afterHeaderDiscard(() => switchProject(id)).catch(setError),
  // A restore already wrote the project's files; the header asked the discard
  // confirm, so the reopen must not ask a second time.
  onRestore: (id) => afterHeaderDiscard(() => switchProject(id)).catch(setError),
  onFileCreate: () => switchProject(currentProjectId).catch(setError),
  writeFile: (projectId, path, content) => storageSession.writeThrough(projectId, path, content, { message: 'edit', coalesceKey: 'edit', coalesceMs: EDIT_COALESCE_MS }),
  onDropOnProject: async (id, dataTransfer) => {
    const entries = await extractEntries(dataTransfer)
    await projectManager.mergeDrop(id, entries, { readDir, readAsText }).catch(setError)
    if (id === currentProjectId) await switchProject(id).catch(setError)
    editorHeader.render()
  },
  onError: setError,
  onFlip: () => editorHeader.render(),
  canUseRowboat: (await getRowboatStore()) !== null,
  confirm,
  confirmDiscard: () => editor.confirmDiscard(),
  syncDeps: accountDeps,
  onDelete: async (id) => {
    await projectManager.deleteProject(id)
    if (id === currentProjectId) {
      const next = pickBootProject(await projectManager.listAll())
      if (next) {
        await switchProject(next).catch(setError)
        await editorHeader.select(next)
      } else {
        const created = await projectManager.createProject('Untitled', { entry: 'main.js', files: defaultProjectFiles({ main: defaultCode, hierarchicalCar: hierarchicalCarCode }) })
        await switchProject(created.id).catch(setError)
        await editorHeader.select(created.id)
      }
    }
    await editorHeader.render()
  },
  onRetrySync: () => syncHandle?.syncNow(),
})
// The boot switch above ran before the header existed; the dropdown opens on
// whatever project that left open.
await editorHeader.select(currentProjectId)

// ============== Cleanup on Page Unload ==============
// Call destroy functions to clean up event listeners and resources
window.addEventListener('unload', () => {
  menu.destroy()
  remote.destroy()
  welcome.destroy()
  about.destroy()
  paramsUI.destroyParamsTreeView()
  editor.destroy()
  viewState.viewer?.destroy?.()
  ctrl.destroy() // Clean up OrbitControl event listeners and animation frame
  destroyFrame() // explicit terminate path: rejects pending frame requests and removes the frame
  diskProject?.stop()
  fileWatcher.cleanup() // Explicit cleanup (complements internal beforeunload fallback)
})
